/** Device position for on-screen use (map, arrival, next stop). Sent to dispatch only by DutyProvider, and only on duty. */
import { useEffect, useRef, useState } from 'react';
import { bearingDeg, distanceFeet } from '@civilos/domain';
import { onSerialFix, serialIsFresh, startSerialGps } from './serialgps.ts';

export type Pos = { lat: number; lng: number; accuracy: number; heading: number | null; mph: number | null; at: number; source?: 'serial' | 'browser'; satellites?: number | null };

/**
 * Watches the device position. Some laptops (Chrome on Windows especially) stop delivering
 * watch updates while driving, so if nothing has arrived for 2 s we also ask for a fresh fix.
 */
export function usePosition(): { pos: Pos | null; err: string | null } {
  const [pos, setPos] = useState<Pos | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const prev = useRef<{ lat: number; lng: number } | null>(null);
  const heading = useRef<number | null>(null);
  const lastFix = useRef(0);
  const lastBrowserTs = useRef(0);
  useEffect(() => {
    if (!('geolocation' in navigator)) return setErr('This device has no location service');
    const onFix = (x: GeolocationPosition) => {
      if (serialIsFresh()) return; // the connected GPS is better; ignore Windows/browser location while it's sending
      if (x.timestamp && x.timestamp < lastBrowserTs.current) return; // older than what we have
      lastBrowserTs.current = x.timestamp || 0;
      lastFix.current = Date.now();
      const here = { lat: x.coords.latitude, lng: x.coords.longitude };
      const speed = x.coords.speed;
      if (x.coords.heading != null && Number.isFinite(x.coords.heading) && (speed ?? 0) > 1) heading.current = x.coords.heading;
      // No compass: derive direction of travel from the last two fixes ~60 ft apart.
      else if (prev.current && distanceFeet(prev.current, here) > 60) heading.current = bearingDeg(prev.current, here);
      if (!prev.current || distanceFeet(prev.current, here) > 60) prev.current = here;
      setPos({ ...here, accuracy: Math.round(x.coords.accuracy), heading: heading.current, mph: speed == null ? null : Math.round(speed * 2.237), at: Date.now(), source: 'browser' });
      setErr(null);
    };
    // USB / built-in GPS on a serial port (see serialgps.ts).
    startSerialGps();
    const offSerial = onSerialFix((f) => {
      lastFix.current = f.at;
      const here = { lat: f.lat, lng: f.lng };
      if (f.heading != null && (f.speedMps ?? 0) > 1) heading.current = f.heading;
      else if (prev.current && distanceFeet(prev.current, here) > 60) heading.current = bearingDeg(prev.current, here);
      if (!prev.current || distanceFeet(prev.current, here) > 60) prev.current = here;
      setPos({ ...here, accuracy: f.accuracyM ?? 5, heading: heading.current, mph: f.speedMps == null ? null : Math.round(f.speedMps * 2.237), at: f.at, source: 'serial', satellites: f.satellites });
      setErr(null);
    });
    const started = Date.now();
    const onErr = (e: GeolocationPositionError) => {
      if (e.code === e.PERMISSION_DENIED) setErr('Location is blocked for this site — allow it in the browser and in Windows Settings → Privacy → Location');
      else if (e.code === e.POSITION_UNAVAILABLE && !lastFix.current) setErr('Location unavailable — turn on location in Windows, or ☰ → Connect GPS');
      // A timeout just means "no fix yet": keep trying, and only say so if it's been a while (see the check below).
    };
    // Quick first answer: whatever location the device already has (Wi-Fi / last known), then the precise watch.
    navigator.geolocation.getCurrentPosition(onFix, () => undefined, { enableHighAccuracy: false, maximumAge: Infinity, timeout: 5_000 });
    const id = navigator.geolocation.watchPosition(onFix, onErr, { enableHighAccuracy: true, maximumAge: 3_000, timeout: 60_000 });
    // If the watch goes quiet (some laptops stop sending while driving), ask directly — but patiently: back-to-back
    // requests can keep the location service from ever settling.
    let asking = false;
    const poll = setInterval(() => {
      if (!lastFix.current && Date.now() - started > 45_000) setErr((e) => e ?? 'Still waiting for a location fix… (☰ → Connect GPS if this laptop has a GPS port)');
      if (asking || Date.now() - lastFix.current < 6_000) return;
      asking = true;
      navigator.geolocation.getCurrentPosition(
        (x) => ((asking = false), onFix(x)),
        (e) => ((asking = false), onErr(e)),
        { enableHighAccuracy: true, maximumAge: 3_000, timeout: 20_000 },
      );
    }, 3_000);
    return () => (navigator.geolocation.clearWatch(id), clearInterval(poll), offSerial());
  }, []);
  return { pos, err };
}

export function pinOf(p: any): { lat: number; lng: number } | null {
  const a = p.address;
  if (a.pin_lat != null && a.pin_lng != null) return { lat: a.pin_lat, lng: a.pin_lng };
  if (a.lat != null && a.lng != null) return { lat: a.lat, lng: a.lng };
  return null;
}

/**
 * The attempt is allowed, but an unsuccessful one wouldn't count toward non-service (same window already
 * covered, or less than a full day after another counted attempt). Returns the short reason, or null.
 */
export function wontCount(ev: any): string | null {
  const w = (ev?.attemptNow?.warnings ?? []).find((x: any) => x.code === 'WINDOW_ALREADY_COVERED' || x.code === 'ATTEMPT_SPACING');
  if (!w) return null;
  const m = /will not count toward non-service: (.*?)\. Non-service/.exec(w.message);
  return m ? m[1]! : 'it does not fill a new time window';
}
